import type { Unsubscribe } from 'firebase/firestore';
import {
  AgentOperatorBackrefSchema,
  AgentProfileSchema,
  EarningsLedgerEntrySchema,
  type AgentProfile,
} from '@kilat/shared';
import { createRepo } from './_base.js';

export type { AgentOperatorBackref } from '@kilat/shared';

/**
 * Agents collection — gated to `isThisAgent(agentId) || isSuperAdmin()` by
 * firestore.rules. Super-admin reads + writes directly (no admin SDK callable
 * needed): a `claimsSyncAgent` Cloud Function watches `agents/{id}` writes and
 * keeps the agent's custom claims in sync (e.g. revokes `agent:true` when
 * status flips to suspended/terminated). So a super-admin can change agent
 * status / tier / KYC purely client-side and the claim propagation is automatic.
 */
export const agentsRepo = createRepo({
  path: 'agents',
  schema: AgentProfileSchema,
  name: 'agents',
});

/**
 * Live roster of ONE platform brand's agents. The `agents/{id}` read rule is
 * `isThisAgent(id) || isSuperAdminOf(resource.data.platformBrandId)`, and
 * Firestore evaluates a LIST query against the rule, so the unfiltered
 * `listSnapshot()` is denied for every super-admin. Filtering on the caller's
 * brand is what lets the rule prove every row is theirs (ADR-0017).
 */
export function subscribeAgentsForBrand(
  brandId: string,
  cb: (agents: AgentProfile[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return agentsRepo.listSnapshotBounded(
    { where: [['platformBrandId', '==', brandId]] },
    cb,
    onError,
  );
}

/**
 * `agents/{id}/earningsLedger/{entryId}` — read-only on the dashboard side
 * (writes come from billing / commission Cloud Functions). Used by the
 * super-admin detail panel to show accrued totals + recent ledger entries.
 */
export function agentEarningsLedgerRepoFor(agentId: string) {
  return createRepo({
    path: `agents/${agentId}/earningsLedger`,
    schema: EarningsLedgerEntrySchema,
    name: `agents/${agentId}/earningsLedger`,
  });
}

/**
 * `agents/{id}/operators/{tenantId}` — back-reference doc written by
 * tenantBootstrap on agent-attributed signups and patched by
 * `commissionAccrue` on first/subsequent paid invoices. Sprint 40
 * formalised the schema in `@kilat/shared`.
 *
 * Drives the "operators they brought in" list on /superadmin/agents AND
 * the agent PWA's `/operators` page.
 */
export function agentOperatorsRepoFor(agentId: string) {
  return createRepo({
    path: `agents/${agentId}/operators`,
    schema: AgentOperatorBackrefSchema,
    name: `agents/${agentId}/operators`,
  });
}
